// ProductGrid.tsx

import { Link } from 'react-router-dom';
import { Box, Button, CircularProgress, Grid, Typography } from '@mui/material';
import { useRef, useEffect } from 'react';
import Pagination from './Pagination';
import ProductsToolbar from './ProductsToolbar';
// 🔥 IMPORT SHARED TYPES
import { MultiFilterMutation, Product } from '../../../../types/Multifilter.types';

interface ProductGridProps {
  productItemCount: number;
  getProductsMutation: MultiFilterMutation; // 🔥 USE SHARED TYPE
  totalPages: number;
  currentPage: number;
  previousPage: () => void;
  nextPage: () => void;
  lastPage: () => void;
  firstPage: () => void;
  anchorRef: React.RefObject<HTMLDivElement>;

  // NEW: Lazy loading props
  showAll?: boolean;
  accumulatedProducts: Product[];
  onLoadMore: () => Promise<void>;
  loadingMore: boolean;
  // The last load-more failed: stop auto-triggering and offer a manual retry.
  loadMoreFailed?: boolean;

  // NEW: Sort and items per page props
  currentSort: string;
  onSortChange: (sortValue: string) => void;
  currentItemsPerPage: number;
  onItemsPerPageChange: (itemsPerPage: number) => void;

  // NEW: Hover animation toggle (from Hub: Filtered Products block config)
  hoverAnimationEnabled?: boolean;

  // NEW: called just before navigating to a PDP so the listing can remember
  // scroll position (and how many items were loaded) for restore on back.
  onProductNavigate?: () => void;
}

const ProductGrid = ({
                       productItemCount,
                       getProductsMutation,
                       totalPages,
                       currentPage,
                       previousPage,
                       nextPage,
                       lastPage,
                       firstPage,
                       anchorRef,
                       showAll = false,
                       accumulatedProducts,
                       onLoadMore,
                       loadingMore,
                       loadMoreFailed = false,
                       currentSort,
                       onSortChange,
                       currentItemsPerPage,
                       onItemsPerPageChange,
                       hoverAnimationEnabled = true,
                       onProductNavigate,
                     }: ProductGridProps) => {
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const loadMoreTriggerRef = useRef<HTMLDivElement>(null);

  // Use accumulated products in Show All mode, otherwise use regular products
  const visibleProducts = showAll && accumulatedProducts.length > 0
    ? accumulatedProducts
    : getProductsMutation.data?.products || [];

  // Lazy-load: the grid now scrolls with the PAGE (like trade), so observe the
  // sentinel against the viewport (root: null) instead of an internal box.
  useEffect(() => {
    // After a failure, don't re-arm: a new observer fires as soon as it's
    // created, which would hit the API again in a tight loop.
    if (!showAll || !loadMoreTriggerRef.current || loadingMore || loadMoreFailed) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const target = entries[0];
        const hasMoreProducts = visibleProducts.length < productItemCount;

        if (target.isIntersecting && hasMoreProducts) {
          onLoadMore();
        }
      },
      {
        // Viewport (page scroll), not an internal container.
        root: null,
        threshold: 0.1,
        rootMargin: '600px'
      }
    );

    observer.observe(loadMoreTriggerRef.current);

    return () => observer.disconnect();
  }, [showAll, loadingMore, loadMoreFailed, visibleProducts.length, productItemCount, onLoadMore]);

  return (
    <>
      {/* Products Toolbar - ALWAYS SHOW */}
      <ProductsToolbar
        productItemCount={productItemCount}
        currentSort={currentSort}
        onSortChange={onSortChange}
        currentItemsPerPage={currentItemsPerPage}
        onItemsPerPageChange={onItemsPerPageChange}
        totalPages={totalPages}
        currentPage={currentPage}
        previousPage={previousPage}
        nextPage={nextPage}
        lastPage={lastPage}
        firstPage={firstPage}
        isLoading={getProductsMutation.isLoading}
        showAll={showAll}
      />

      {/* Product Grid */}
      <Grid
        ref={scrollContainerRef}
        id="multifilter-product-scroll"
        container
        spacing={2}
        pl={0}
        pr={0}
        sx={{
          // Grid scrolls with the PAGE (like trade) — no internal scroll box —
          // so the sticky filter column has room to pin/scroll independently.
          overflowX: 'hidden'
        }}
      >
        {getProductsMutation.isLoading ? (
          <Box
            sx={{
              height: '50rem',
              width: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <CircularProgress />
          </Box>
        ) : getProductsMutation.isError ? (
          <Typography>An error occurred, please try a different filter</Typography>
        ) : (
          <>
            {visibleProducts.map((product, index) => {
              // Format size string based on available data
              const sizeString = product.diameter && product.diameter !== '0cm'
                ? product.diameter.trim()
                : `${product.width?.trim()}x${product.length_cm?.trim()}`;

              return (
                <Grid item xs={6} sm={4} key={index}>
                  <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                    <Box
                      component={Link}
                      to={`../item_group/${product.item_group}/collection/${encodeURIComponent(
                        product.range
                      )}/design/${encodeURIComponent(
                        product.design
                      )}/colour/${encodeURIComponent(product.colour)}?targetSize=${encodeURIComponent(sizeString)}&targetProduct=${encodeURIComponent(product.sku)}`}
                      onClick={() => {
                        // Fires for every click, including modifier/middle clicks — harmless,
                        // it just saves scroll position; the actual navigation for a plain
                        // left click is handled by <Link> itself, and for right-click/middle-click
                        // the browser handles it natively because this is now a real <a href>.
                        onProductNavigate?.();
                      }}
                      sx={{
                        display: 'block',
                        width: '100%',
                        height: { xs: '150px', sm: '300px' },
                        cursor: 'pointer',
                        overflow: 'hidden',
                        textDecoration: 'none',
                        color: 'inherit',
                        ...(hoverAnimationEnabled ? { '&:hover img': { transform: 'scale(1.03)' } } : {}),
                      }}
                    >
                      <img
                        data-product-image="true"
                        style={{
                          width: '100%',
                          height: '100%',
                          objectFit: 'cover',
                          transition: hoverAnimationEnabled ? 'transform 0.5s ease' : undefined,
                        }}
                        src={product.primaryImageUrl}
                        alt={`${product.design} in ${product.colour}`}
                      />
                    </Box>
                    <Typography
                      fontWeight="600"
                      fontSize="18px"
                      lineHeight={'33px'}
                      textTransform={'capitalize'}
                      mt={1}
                      mb={0}
                    >
                      {product.design}
                    </Typography>
                    <Typography fontWeight="400" fontSize="16px">
                      {product.colour}
                    </Typography>
                    {product.web_size !== '-' && (
                      <Typography
                        fontWeight="400"
                        fontSize="14px"
                        mb={1}
                        fontStyle="italic"
                      >
                        {product.web_size}
                      </Typography>
                    )}
                  </Box>
                </Grid>
              );
            })}

            {/* Infinite scroll trigger - triggers API call */}
            {showAll && visibleProducts.length < productItemCount && (
              <Grid item xs={12}>
                <Box
                  ref={loadMoreTriggerRef}
                  sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    py: 4
                  }}
                >
                  {loadingMore ? (
                    <>
                      <CircularProgress size={40} />
                      <Typography sx={{ ml: 2 }}>Loading more products...</Typography>
                    </>
                  ) : loadMoreFailed ? (
                    <>
                      <Typography sx={{ color: 'text.secondary', mr: 2 }}>
                        Couldn't load more products.
                      </Typography>
                      <Button variant="outlined" size="small" onClick={() => onLoadMore()}>
                        Try again
                      </Button>
                    </>
                  ) : (
                    <Typography sx={{ color: 'text.secondary' }}>Scroll to load more...</Typography>
                  )}
                </Box>
              </Grid>
            )}

            {/* Show completion message when all products are loaded */}
            {showAll && visibleProducts.length >= productItemCount && visibleProducts.length > 0 && (
              <Grid item xs={12}>
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                  <Typography variant="body1" color="text.secondary">
                    ✓ All {productItemCount} products loaded
                  </Typography>
                </Box>
              </Grid>
            )}
          </>
        )}
      </Grid>

      {/* Bottom Pagination - Hide when showAll is true */}
      {!showAll && !getProductsMutation.isLoading && !getProductsMutation.isError && visibleProducts.length > 0 && (
        <Box sx={{ mt: 3, mb: 2, display: 'flex', justifyContent: 'flex-start', borderTop: 1, borderColor: 'divider', pt: 2 }}>
          <Pagination
            totalPages={totalPages}
            currentPage={currentPage}
            previousPage={previousPage}
            nextPage={nextPage}
            productItemCount={productItemCount}
            lastPage={lastPage}
            firstPage={firstPage}
          />
        </Box>
      )}
    </>
  );
};

export default ProductGrid;